// Search results: filter items by query (category name, family name, item name, sales_format, brand name).
// Uses items, families, categories and junction tables; shows list of matching items with link to product.

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSourcingItems, useProductFamilies, useCategories, useSourcingItemFamilies, useProductFamilyCategories, useBrands } from '@/hooks/use-store';
import { safeTrim, searchMatch } from '@/lib/utils';
import { StoreThumbnailImg } from '@/components/store/StoreThumbnailImg';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Search } from 'lucide-react';

export interface StoreSearchResultsProps {
  query: string;
}

export function StoreSearchResults({ query }: StoreSearchResultsProps) {
  const q = safeTrim(query);
  const { data: items, isLoading } = useSourcingItems(null);
  const { data: families } = useProductFamilies(null);
  const { data: categories } = useCategories();
  const { data: brands } = useBrands();
  const { data: itemFamilies } = useSourcingItemFamilies();
  const { data: familyCategories } = useProductFamilyCategories();

  const results = useMemo(() => {
    if (!q || q.length < 2 || !items?.length) return [];
    const getItemCategoryFamily = (itemId: string) => {
      const familyId = itemFamilies?.find((j) => j.item_id === itemId)?.family_id;
      const categoryId = familyId ? familyCategories?.find((f) => f.family_id === familyId)?.category_id : undefined;
      const family = familyId ? families?.find((f) => f.id === familyId) : null;
      const category = categoryId ? categories?.find((c) => c.id === categoryId) : null;
      return { family, category };
    };
    return items.filter((item) => {
      const { family, category } = getItemCategoryFamily(item.id);
      const brand = item.brand_id ? brands?.find((b) => b.id === item.brand_id) : null;
      const inName = searchMatch(item.internal_name, q);
      const inFormat = searchMatch(item.sales_format, q);
      const inFamily = searchMatch(family?.family_name, q);
      const inCategory = searchMatch(category?.name, q);
      const inBrand = searchMatch(brand?.name, q);
      return inName || inFormat || inFamily || inCategory || inBrand;
    });
  }, [q, items, families, categories, brands, itemFamilies, familyCategories]);

  if (!q) return null;
  if (q.length < 2) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 text-sm">
        <Search className="h-4 w-4" />
        Escribe al menos 2 caracteres para buscar.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-zinc-900">Buscando...</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 rounded-lg bg-zinc-100 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-zinc-900">
        Resultados de búsqueda {results.length > 0 ? `(${results.length})` : ''}
      </h2>
      {results.length === 0 ? (
        <p className="text-zinc-600">No hay resultados para &quot;{q}&quot;.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {results.map((item) => {
            const familyId = itemFamilies?.find((j) => j.item_id === item.id)?.family_id;
            const categoryId = familyId ? familyCategories?.find((f) => f.family_id === familyId)?.category_id : undefined;
            const family = familyId ? families?.find((f) => f.id === familyId) : null;
            const category = categoryId ? categories?.find((c) => c.id === categoryId) : null;

            return (
              <Link key={item.id} to={`/store/product/${item.slug}`}>
                <Card className="overflow-hidden border-zinc-200 hover:shadow-md transition-shadow h-full">
                  {item.packshot_image && (
                    <div className="aspect-video w-full flex items-center justify-center overflow-hidden rounded-t-lg bg-white border-b border-zinc-200">
                      <StoreThumbnailImg
                        coverUrl={item.packshot_image}
                        size={{ width: 320, height: 180 }}
                        className="max-h-full max-w-full w-auto h-auto object-contain"
                      />
                    </div>
                  )}
                  <CardHeader className="font-semibold text-zinc-900 pb-1">
                    {item.internal_name}
                  </CardHeader>
                  <CardContent className="text-sm text-zinc-600">
                    {[category?.name, family?.family_name].filter(Boolean).join(' · ')}
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
